
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";

import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

const ticketBalanceOptions = [
  0,
  1,
  2,
  3,
  5,
  10,
  15,
  20,
  25,
  50,
];

export default function AdminPayersWithLowBalanceReport() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [timeRange, setTimeRange] = useState({
    start: "",
    end: "",
  });

  const [maxTicketBalance, setMaxTicketBalance] =
    useState("5");

  const [searchFilter, setSearchFilter] =
    useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleRangeChange = useCallback(
    ({ start, end }) => {
      setTimeRange({ start, end });
    },
    []
  );

  const fetchReport = useCallback(async () => {
    if (
      !timeRange.start ||
      !timeRange.end ||
      maxTicketBalance === ""
    ) {
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${localStorage.getItem("token")}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/payers-with-low-balance`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
            max_ticket_balance:
              Number(maxTicketBalance),
          },
        }
      );

      setRows(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "Error fetching payers at risk report:",
        error
      );

      setRows([]);

      setMessage(
        "❌ " +
          (
            error.response?.data?.detail ||
            t(
              "admin_payers_with_low_balance_report.messages.fetch_failed"
            )
          )
      );
    } finally {
      setLoading(false);
    }
  }, [
    maxTicketBalance,
    timeRange.end,
    timeRange.start,
    t,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    const search =
      searchFilter.trim().toLowerCase();

    if (!search) {
      setFilteredRows(rows);
      return;
    }

    const filtered = rows.filter((row) => {
      const searchableValues = [
        row.user_id,
        row.userno,
        row.fullname,
        row.email,
        row.user_tax_id,
        row.sponsor,
        row.sponsor_no,
        row.wallet_id,
        row.walletname,
        row.route_id,
        row.route_name,
        row.start_point,
        row.end_point,
        row.ticket_balance,
        row.log_count,
        row.profile_notes,
      ];

      return searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search)
      );
    });

    setFilteredRows(filtered);
  }, [rows, searchFilter]);

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    return new Date(value)
      .toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  return (
    <StandardScreen
      topNav={
        <TopNav
          title={t(
            "admin_payers_with_low_balance_report.title"
          )}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card
              title={t(
                "admin_payers_with_low_balance_report.title"
              )}
            >
              <Stack gap="3">
                <FormField>
                  <TimeRangeSelector
                    onRangeChange={
                      handleRangeChange
                    }
                  />
                </FormField>

                <FormField
                  label={t(
                    "admin_payers_with_low_balance_report.fields.max_ticket_balance.label"
                  )}
                >
                  <SelectInput
                    value={maxTicketBalance}
                    onChange={(event) =>
                      setMaxTicketBalance(
                        event.target.value
                      )
                    }
                  >
                    {ticketBalanceOptions.map(
                      (balance) => (
                        <option
                          key={balance}
                          value={balance}
                        >
                          {t(
                            "admin_payers_with_low_balance_report.fields.max_ticket_balance.option",
                            {
                              count: balance,
                            }
                          )}
                        </option>
                      )
                    )}
                  </SelectInput>
                </FormField>

                <FormField
                  label={t(
                    "admin_payers_with_low_balance_report.fields.search.label"
                  )}
                >
                  <TextInput
                    type="text"
                    value={searchFilter}
                    onChange={(event) =>
                      setSearchFilter(
                        event.target.value
                      )
                    }
                    placeholder={t(
                      "admin_payers_with_low_balance_report.fields.search.placeholder"
                    )}
                  />
                </FormField>

                {message && (
                  <div>{message}</div>
                )}
              </Stack>
            </Card>

            <Card>
              <TableShell maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>
                      <Th
                        columnIndex={0}
                        sticky
                      >
                        {t(
                          "admin_payers_with_low_balance_report.table.fullname"
                        )}
                      </Th>

                      <Th
                        columnIndex={1}
                        sticky
                      >
                        {t(
                          "admin_payers_with_low_balance_report.table.userno"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.sponsor"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.sponsor_no"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.wallet"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.route"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.start_point"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.end_point"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.ticket_balance"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.log_count"
                        )}
                      </Th>

                      <Th>
                        {t(
                          "admin_payers_with_low_balance_report.table.last_activity"
                        )}
                      </Th>
                    </tr>
                  </thead>

                  <tbody>
                    {!loading &&
                      filteredRows.map((row) => (
                        <tr
                          key={`${row.user_id}-${row.wallet_id}`}
                        >
                          <Td
                            columnIndex={0}
                            sticky
                          >
                            {row.fullname || "-"}
                          </Td>

                          <Td
                            columnIndex={1}
                            sticky
                          >
                            {row.userno || "-"}
                          </Td>

                          <Td>
                            {row.sponsor || "-"}
                          </Td>

                          <Td>
                            {row.sponsor_no || "-"}
                          </Td>

                          <Td>
                            {row.walletname || "-"}
                          </Td>

                          <Td>
                            {row.route_name || "-"}
                          </Td>

                          <Td>
                            {row.start_point || "-"}
                          </Td>

                          <Td>
                            {row.end_point || "-"}
                          </Td>

                          <NumberTd>
                            {row.ticket_balance}
                          </NumberTd>

                          <NumberTd>
                            {row.log_count}
                          </NumberTd>

                          <Td>
                            {formatDateTime(
                              row.last_activity
                            )}
                          </Td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </TableShell>

              {loading && (
                <div
                  style={{
                    padding: "1rem",
                    textAlign: "center",
                  }}
                >
                  {t(
                    "admin_payers_with_low_balance_report.messages.loading"
                  )}
                </div>
              )}

              {!loading &&
                filteredRows.length === 0 && (
                  <div
                    style={{
                      padding: "1rem",
                      textAlign: "center",
                    }}
                  >
                    {t(
                      "admin_payers_with_low_balance_report.messages.empty"
                    )}
                  </div>
                )}
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}
